import React, { useId } from 'react';
import { BADGE_POSITIONS, STATUS } from './zoneStyles';

const NEUTRAL = {
  area: { fill: '#E3E8EE', stroke: '#C3CAD3' },
  marker: { fill: '#CBD3DC', stroke: 'none' },
  wheel: { fill: '#9AA3AE', stroke: 'none' },
  disc: { fill: '#B9C1CA', stroke: '#9AA3AE' },
  light: { fill: '#FFFFFF', stroke: '#8C96A3' },
};

/**
 * A top-down outline of a vehicle with each part shaded by what happened to it (see zoneStyles.STATUS).
 *
 * @param {Object<string, string>} statuses - zone key -> "serviced" | "due" | "declined"; parts not listed stay neutral
 * @param {Object<string, number>} [numbers] - zone key -> the number shown on that part, matching a list beside it
 * @param {number} [width] - rendered width in px; the outline keeps its 1:2 proportions
 * @param {number} [badgeScale] - enlarges the number badges when the outline is drawn small, so they stay legible.
 *   At most 1.25: the closest two badge positions are 29 units apart.
 * @param {string} label - what a screen reader hears instead of the drawing
 */
const VehicleDiagram = ({ statuses, numbers = {}, width = 220, badgeScale = 1, label }) => {
  // One pattern id per drawing: several diagrams can share a page.
  const hatchId = `declinedHatch${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  const look = (zoneKey, neutral) => {
    const status = STATUS[statuses[zoneKey]];
    if (!status) return { fill: neutral.fill, stroke: neutral.stroke };
    return {
      fill: status.hatch ? `url(#${hatchId})` : status.fill,
      stroke: status.stroke,
      strokeDasharray: status.dash || undefined,
      strokeWidth: status.stroke === 'none' ? undefined : 2,
    };
  };

  const wheels = [
    [18, 64],
    [196, 64],
    [18, 330],
    [196, 330],
  ];
  const discs = [
    [31, 101],
    [209, 101],
    [31, 367],
    [209, 367],
  ];
  const axleStubs = [
    [40, 96],
    [184, 96],
    [40, 362],
    [184, 362],
  ];

  return (
    <svg width={width} height={width * 2} viewBox="0 0 240 480" role="img" aria-label={label}>
      <defs>
        <pattern id={hatchId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" fill={STATUS.declined.tint} />
          <line x1="0" y1="0" x2="0" y2="6" stroke={STATUS.declined.stroke} strokeWidth="3" />
        </pattern>
      </defs>

      {wheels.map(([x, y]) => (
        <rect key={`wheel-${x}-${y}`} x={x} y={y} width="26" height="74" rx="9" {...look('tires', NEUTRAL.wheel)} />
      ))}

      <rect x="40" y="22" width="160" height="436" rx="54" fill="#F4F5F7" stroke="#8C96A3" strokeWidth="2" />

      {axleStubs.map(([x, y]) => (
        <rect key={`axle-${x}-${y}`} x={x} y={y} width="16" height="10" rx="3" {...look('alignment', NEUTRAL.marker)} />
      ))}

      {/* engine bay and what sits in it */}
      <rect x="56" y="44" width="128" height="104" rx="26" {...look('engine_oil', NEUTRAL.area)} />
      <rect x="66" y="58" width="38" height="24" rx="6" {...look('engine_air_filter', NEUTRAL.marker)} />
      <rect x="140" y="58" width="34" height="24" rx="6" {...look('battery', NEUTRAL.marker)} />
      <rect x="96" y="96" width="48" height="26" rx="6" {...look('engine_performance', NEUTRAL.marker)} />
      <rect x="100" y="27" width="40" height="10" rx="3" {...look('cooling', NEUTRAL.marker)} />

      {/* lights */}
      <rect x="58" y="28" width="36" height="9" rx="4" {...look('lights', NEUTRAL.light)} />
      <rect x="146" y="28" width="36" height="9" rx="4" {...look('lights', NEUTRAL.light)} />
      <rect x="58" y="444" width="36" height="8" rx="4" {...look('lights', NEUTRAL.light)} />
      <rect x="146" y="444" width="36" height="8" rx="4" {...look('lights', NEUTRAL.light)} />

      {/* cowl, windshield and wipers */}
      <rect x="60" y="152" width="120" height="12" rx="4" {...look('cabin_air_filter', NEUTRAL.marker)} />
      <path d="M64 168 L176 168 L166 214 L74 214 Z" fill="#DCE3EA" stroke="#B4BDC8" />
      {[
        [78, 207, 116, 186],
        [124, 207, 162, 186],
      ].map(([x1, y1, x2, y2]) => (
        <line
          key={`wiper-${x1}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={STATUS[statuses.wipers]?.line || NEUTRAL.wheel.fill}
          strokeWidth="5"
          strokeLinecap="round"
        />
      ))}

      {/* cabin, climate, drivetrain tunnel */}
      <rect x="70" y="218" width="100" height="116" rx="10" fill="#E9ECF0" stroke="#C3CAD3" />
      <rect x="78" y="226" width="26" height="18" rx="4" {...look('climate', NEUTRAL.marker)} />
      <rect x="109" y="226" width="22" height="100" rx="6" {...look('drivetrain', NEUTRAL.marker)} />

      {/* rear */}
      <path d="M74 338 L166 338 L174 374 L66 374 Z" fill="#DCE3EA" stroke="#B4BDC8" />
      <rect x="58" y="378" width="124" height="62" rx="18" fill="#E9ECF0" stroke="#C3CAD3" />

      {discs.map(([cx, cy]) => (
        <circle key={`disc-${cx}-${cy}`} cx={cx} cy={cy} r="9" {...look('brakes', NEUTRAL.disc)} />
      ))}

      {Object.entries(numbers).map(([zoneKey, number]) => {
        const [cx, cy] = BADGE_POSITIONS[zoneKey] || [];
        if (cx === undefined) return null;
        return (
          <g key={`badge-${zoneKey}`}>
            <circle
              cx={cx}
              cy={cy}
              r={11 * badgeScale}
              fill="#FFFFFF"
              stroke={STATUS[statuses[zoneKey]]?.color || '#5f5f5f'}
              strokeWidth="2"
            />
            <text x={cx} y={cy + 4 * badgeScale} textAnchor="middle" fontSize={12 * badgeScale} fontWeight="700" fill="#1D252D">
              {number}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default VehicleDiagram;
